import React from 'react';
import { useTranslation } from 'react-i18next';
import { BuildingTypeValue } from '../types';
import EdgeFadeScroller from '../../../../common/EdgeFadeScroller/EdgeFadeScroller';

interface BuildingConfirmStepProps {
  // Street + "postal city" (postal before the city); shown on the building's
  // address line below the map.
  addressWithPostal: string;
  buildingType: BuildingTypeValue;
  onBuildingTypeChange: (value: BuildingTypeValue) => void;
  onNext: () => void;
  requiresManualBuildingSelection: boolean;
  hasSelectedBuilding: boolean;
  // A clicked building's address is still resolving, so there is nothing to confirm yet.
  isSelectionPending?: boolean;
  isSelectionFailed?: boolean;
}

/**
 * Building-confirm chrome (hint, address line, type pills, Next CTA) rendered
 * below the shared FlowMap, which the flow owns and positions above this step.
 * Next stays disabled while a clicked building is still resolving, and when
 * its lookup found no address the hint says so instead (JAA-410).
 */
const BuildingConfirmStep = ({
  addressWithPostal,
  buildingType,
  onBuildingTypeChange,
  onNext,
  requiresManualBuildingSelection,
  hasSelectedBuilding,
  isSelectionPending = false,
  isSelectionFailed = false,
}: BuildingConfirmStepProps) => {
  const { t } = useTranslation();

  const buildingTypes: { value: BuildingTypeValue; label: string }[] = [
    { value: 'apartment_building', label: t('newLandingPage.addMyPlace.building.typeApartment') },
    { value: 'terraced_house', label: t('newLandingPage.addMyPlace.building.typeRowHouse') },
    { value: 'duplex', label: t('newLandingPage.addMyPlace.building.typeDuplex') },
    { value: 'detached_house', label: t('newLandingPage.addMyPlace.building.typeDetached') },
    { value: 'other', label: t('newLandingPage.addMyPlace.building.typeOther') },
  ];

  const hideNextCta = requiresManualBuildingSelection && !hasSelectedBuilding;
  const hintKey = requiresManualBuildingSelection
    ? 'newLandingPage.addMyPlace.building.selectBuildingOnMap'
    : 'newLandingPage.addMyPlace.building.notYourBuilding';
  const hint = isSelectionFailed ? (
    <p className="amp-building__hint amp-building__hint--error" role="alert">
      {t('newLandingPage.addMyPlace.building.pickNoAddress')}
    </p>
  ) : (
    <p className="amp-building__hint">{t(hintKey)}</p>
  );

  return (
    <div className="amp-step amp-step--building">
      <div className="amp-building__body">
        {hint}

        <span className="amp-building__label">{t('newLandingPage.addMyPlace.building.buildingLabel')}</span>
        <p className="amp-building__address">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M9 3.75C10.6569 3.75 12 5.09315 12 6.75C12 8.40685 10.6569 9.75 9 9.75C7.34315 9.75 6 8.40685 6 6.75C6 5.09315 7.34315 3.75 9 3.75ZM9 5.25C8.17157 5.25 7.5 5.92157 7.5 6.75C7.5 7.57843 8.17157 8.25 9 8.25C9.82843 8.25 10.5 7.57843 10.5 6.75C10.5 5.92157 9.82843 5.25 9 5.25Z" fill="#5B6970" />
            <path fillRule="evenodd" clipRule="evenodd" d="M9 0C12.7279 0 15.7499 2.94205 15.75 6.57129C15.75 12.3213 9.84375 17.25 9 17.25L8.95679 17.2463C7.97311 17.0957 2.25 12.2314 2.25 6.57129C2.25008 2.94205 5.27213 0 9 0ZM9 1.5C6.06239 1.5 3.75008 3.80814 3.75 6.57129C3.75 8.95354 4.99136 11.2939 6.47388 13.1221C7.20158 14.0194 7.94717 14.7435 8.54077 15.2307C8.71482 15.3736 8.86964 15.4896 9 15.5815C9.13036 15.4896 9.28518 15.3736 9.45923 15.2307C10.0528 14.7435 10.7984 14.0194 11.5261 13.1221C13.0086 11.2939 14.25 8.95354 14.25 6.57129C14.2499 3.80814 11.9376 1.5 9 1.5Z" fill="#5B6970" />
          </svg>
          {addressWithPostal}
        </p>

        {/* Five types no longer fit one mobile row (JAA-324), so the row scrolls
            behind an edge fade rather than hiding the overflow with no cue. */}
        <EdgeFadeScroller className="amp-pills">
          {buildingTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              className={`amp-pill${buildingType === type.value ? ' amp-pill--selected' : ''}`}
              onClick={() => onBuildingTypeChange(type.value)}
            >
              {type.label}
            </button>
          ))}
        </EdgeFadeScroller>
      </div>

      <div className="amp-step__footer">
        <button
          type="button"
          className={`amp-cta${hideNextCta ? ' amp-cta--hidden' : ''}`}
          disabled={hideNextCta || isSelectionPending || isSelectionFailed}
          onClick={onNext}
        >
          {t('newLandingPage.addMyPlace.building.next')}
        </button>
      </div>
    </div>
  );
};

export default BuildingConfirmStep;
